import { FC, useEffect, useMemo } from "react"

import { useAuthStore } from "stores"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"
import { PayrollJournalEnhancedTable } from "pages/dashboard/documents/reports/component/payroll_journal/PayrollJournalEnhancedTable"

const normalizeDescription = (value?: string | null) =>
  (value || "").trim().replace(/\s+/g, " ").toUpperCase()

const getRetroBaseDescription = (value?: string | null) =>
  normalizeDescription(value).replace(/\bRETRO\b/g, "").replace(/\s+/g, " ").trim()

const isRetroDescription = (value?: string | null) =>
  normalizeDescription(value).includes("RETRO")

const sortRowsWithRetrosNextToBase = <T extends { description?: string | null }>(
  rows: T[] = []
) => {
  const ordered: T[] = []
  const used = new Set<T>()
  const consolidatedSalary = rows.find(
    (row) => normalizeDescription(row.description) === "CONSOLIDATED SALARY"
  )
  const regularRows = rows
    .filter(
      (row) =>
        normalizeDescription(row.description) !== "CONSOLIDATED SALARY" &&
        !isRetroDescription(row.description)
    )
    .sort((a, b) =>
      normalizeDescription(a.description).localeCompare(
        normalizeDescription(b.description)
      )
    )
  const retroRows = rows
    .filter((row) => isRetroDescription(row.description))
    .sort((a, b) =>
      normalizeDescription(a.description).localeCompare(
        normalizeDescription(b.description)
      )
    )

  const pushRowWithRetros = (row: T) => {
    if (used.has(row)) return

    ordered.push(row)
    used.add(row)

    retroRows.forEach((retroRow) => {
      if (
        !used.has(retroRow) &&
        getRetroBaseDescription(retroRow.description) ===
          getRetroBaseDescription(row.description)
      ) {
        ordered.push(retroRow)
        used.add(retroRow)
      }
    })
  }

  if (consolidatedSalary) {
    pushRowWithRetros(consolidatedSalary)
  }

  regularRows.forEach(pushRowWithRetros)
  retroRows.forEach(pushRowWithRetros)

  return ordered
}

const orderKnownRows = <T extends { description?: string | null }>(
  rows: T[],
  knownOrder: string[]
) =>
  knownOrder.flatMap((knownDescription) =>
    sortRowsWithRetrosNextToBase(
      rows.filter((row) => {
        const normalized = normalizeDescription(row.description)
        return (
          normalized === knownDescription ||
          getRetroBaseDescription(row.description) === knownDescription
        )
      })
    )
  )

const isTaxRow = (row: { description?: string | null }) =>
  normalizeDescription(row.description).includes("TAX")

const getDisplayTaxDescription = (description: string) =>
  description.replace(/^(tax\s+)+/i, "Tax ")

const isAssociationRow = (row: { description?: string | null }) =>
  normalizeDescription(row.description).includes("ASSOCIATION")

const isEmployeeVoluntaryContributionRow = (row: {
  description?: string | null
}) => {
  const normalizedDescription = normalizeDescription(row.description)
  return (
    normalizedDescription.includes("VOLUNTARY") ||
    normalizedDescription.includes("EVC")
  )
}

const isAdvanceDeductionRow = (row: {
  description?: string | null
  payroll_item_type_id?: number
  amount?: number | string | null
}) => {
  const normalizedDescription = normalizeDescription(row?.description)
  return (
    Number(row?.payroll_item_type_id) === 8 &&
    (normalizedDescription.startsWith("TAX ") ||
      normalizedDescription.includes("ADVANCE") ||
      !Number(row?.amount || 0))
  )
}

const isEarningSummaryRow = (row: {
  payroll_item_type_id?: number
  amount?: number | string | null
}) => {
  const typeId = Number(row?.payroll_item_type_id)

  if (typeId === 8) return !isAdvanceDeductionRow(row)

  return [1, 3, 4, 7].includes(typeId)
}

const isDeductionSummaryRow = (row: {
  payroll_item_type_id?: number
  amount?: number | string | null
}) => Number(row?.payroll_item_type_id) === 2 || isAdvanceDeductionRow(row)

const parseSummaryAmount = (value?: number | string | null) => {
  if (typeof value === "number") return value
  return Number(String(value || 0).replace(/,/g, "")) || 0
}

const formatSummaryAmount = (value: number) =>
  value.toLocaleString([], { minimumFractionDigits: 2 })

const buildOandoTaxSummaryRows = (rows: any[] = []) => {
  const earningRows = rows.filter(isEarningSummaryRow)
  const sumTax = (predicate: (row: any) => boolean) =>
    earningRows
      .filter(predicate)
      .reduce((total, row) => total + parseSummaryAmount(row?.tax_amount), 0)
  const sumTaxCount = (predicate: (row: any) => boolean) =>
    earningRows
      .filter(predicate)
      .reduce(
        (total, row) =>
          total +
          Number(
            row?.tax_count ||
              (parseSummaryAmount(row?.tax_amount) ? row?.count || 0 : 0)
          ),
        0
      )
  const makeTaxRow = (
    description: string,
    predicate: (row: any) => boolean
  ) => ({
    description,
    amount: formatSummaryAmount(sumTax(predicate)),
    count: sumTaxCount(predicate),
    payroll_item_type_id: 2,
  })

  const hasRetroTax = earningRows.some(
    (row) =>
      isRetroDescription(row?.description) &&
      parseSummaryAmount(row?.tax_amount)
  )

  return [
    makeTaxRow("Consolidated Salary Tax", (row) =>
      normalizeDescription(row?.description) === "CONSOLIDATED SALARY"
    ),
    ...(hasRetroTax
      ? [
          makeTaxRow("Tax Retro", (row) =>
            isRetroDescription(row?.description)
          ),
        ]
      : []),
    makeTaxRow(
      "Total Allowance Tax",
      (row) =>
        normalizeDescription(row?.description) !== "CONSOLIDATED SALARY" &&
        !isRetroDescription(row?.description)
    ),
    makeTaxRow("Tax", () => true),
  ]
}

const sortDeductionSummaryRows = <T extends { description?: string | null }>(
  rows: T[] = []
) => {
  const pensionRows = orderKnownRows(rows, ["PENSION"])
  const voluntaryRows = sortRowsWithRetrosNextToBase(
    rows.filter(
      (row) =>
        isEmployeeVoluntaryContributionRow(row) && !pensionRows.includes(row)
    )
  )
  const nhfRows = orderKnownRows(rows, ["NHF"])
  const statutoryRows = [...pensionRows, ...voluntaryRows, ...nhfRows]
  const taxRows = rows.filter(isTaxRow)
  const associationRows = sortRowsWithRetrosNextToBase(
    rows.filter(isAssociationRow)
  )
  const otherRows = rows.filter(
    (row) =>
      !statutoryRows.includes(row) &&
      !isTaxRow(row) &&
      !isAssociationRow(row)
  )

  return [
    ...statutoryRows,
    ...taxRows,
    ...associationRows,
    ...sortRowsWithRetrosNextToBase(otherRows),
  ]
}

const sortInfoSummaryRows = <T extends { description?: string | null }>(
  rows: T[] = []
) => {
  const statutoryRows = orderKnownRows(rows, ["PENSION", "GRATUITY"])
  const otherRows = rows.filter((row) => !statutoryRows.includes(row))

  return [...statutoryRows, ...sortRowsWithRetrosNextToBase(otherRows)]
}

const getSummaryDescription = (row: any, section: "deduction" | "info") => {
  const description = row?.description || ""
  if (getRetroBaseDescription(description) !== "PENSION") {
    return getDisplayTaxDescription(description)
  }

  const isRetro = isRetroDescription(description)

  if (section === "deduction") {
    return isRetro ? "Employee Pension Retro" : "Employee Pension"
  }

  return isRetro ? "Employer Pension Retro" : "Employer Pension"
}

type Props = {
  data: IPayrollJournalData
  format: "compact" | "detailed" | "summary"
  deductions: string[]
  earnings: string[]
  amortizations: string[]
  contributions: string[]
  uniquePayrolls: number[]
  payPeriods: any[]
  reimbursements?: string[]
  employeeAmortizations: any[]
  earningsTableData: any[]
}

export const DetailsSummary: FC<Props> = ({
  data,
  format,
  uniquePayrolls,
  earnings,
  deductions,
  contributions,
  amortizations,
  payPeriods,
  employeeAmortizations,
  earningsTableData,
  reimbursements = [],
}) => {
  const { user } = useAuthStore()

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
    },
  ]

  const employeeEarningsData =
    earnings
      ?.filter((v) => !reimbursements?.includes(v))
      .sort((a, b) => a.localeCompare(b))
      .concat(reimbursements || []) || []

  const isOandoSummary =
    String(user?.userOrganisationId) === "354" &&
    earnings?.some(
      (description) => normalizeDescription(description) === "CONSOLIDATED SALARY"
    )

  let earningsSummaryRows = useMemo(() => {
    if (isOandoSummary) {
      return (earningsTableData || []).filter(isEarningSummaryRow)
    }

    return (earningsTableData || []).filter((it) =>
      [1, 3, 4, 7, 8].includes(Number(it?.payroll_item_type_id))
    )
  }, [earningsTableData, isOandoSummary])

  earningsSummaryRows = useMemo(() => {
    if (isOandoSummary) {
      const regularRows = earningsSummaryRows.filter(
        (row) => Number(row?.payroll_item_type_id) !== 7
      )
      const reimbursementRows = earningsSummaryRows.filter(
        (row) => Number(row?.payroll_item_type_id) === 7
      )
      return [
        ...sortRowsWithRetrosNextToBase(regularRows),
        ...sortRowsWithRetrosNextToBase(reimbursementRows),
      ]
    }

    return earningsSummaryRows
      .filter((it) => normalizeDescription(it.description) === "CONSOLIDATED SALARY")
      .concat(
        earningsSummaryRows
          .filter(
            (it) =>
              normalizeDescription(it.description) !== "CONSOLIDATED SALARY"
          )
          .sort((a, b) => a.description.localeCompare(b.description))
      )
  }, [earningsSummaryRows, isOandoSummary])

  const deductionsSummaryRows = useMemo(() => {
    const rows = (earningsTableData || []).filter((it) =>
      isOandoSummary
        ? isDeductionSummaryRow(it)
        : Number(it?.payroll_item_type_id) === 2
    )

    return isOandoSummary
      ? sortDeductionSummaryRows([
          ...rows.filter((row) => !isTaxRow(row)),
          ...buildOandoTaxSummaryRows(earningsTableData),
        ])
      : rows
  }, [earningsTableData, isOandoSummary])

  const infoSummaryRows = useMemo(() => {
    const infoIds = [6, 5]
    const rows = (earningsTableData || []).filter(
      (it) => it.payroll_item_type_id && infoIds.includes(it.payroll_item_type_id)
    )

    return isOandoSummary ? sortInfoSummaryRows(rows) : rows
  }, [earningsTableData, isOandoSummary])

  if (payPeriods?.length)
    return (
      <div className="flex flex-col gap-2 w-full font-avenir">
        <div className="w-full">
          <div className="text-lg text-neutral500 font-semibold">
            Employee Earnings
          </div>
          <div className="w-full">
            {payPeriods?.map((x, idx) => (
              <div
                key={x?.payroll_item_type_id ?? idx}
                className="mt-[40px] w-full flex gap-4 flex-col"
              >
                {/* <div className="flex gap-2 items-center w-full my-3 period-title">
                  <div className="text-[16px] font-[700] text-neutral500">
                    {x?.payroll_month} Payroll
                    <br />
                  </div>
                  <div className="text-sm text-neutral500 font-semibold">
                    Payroll period: {x?.pay_period}
                  </div>
                </div> */}

                {format !== "summary" && (
                  <div className="w-full max-w-full">
                    <PayrollJournalEnhancedTable
                      data={data}
                      rows={x?.employeeData || []}
                      rowType="Employee"
                      user={user}
                      title={`${x?.payroll_month} payroll - ${x?.pay_period}`}
                      variant={format}
                    />
                  </div>
                )}
                {format === "summary" && (
                  <div>
                    <SummaryTable
                      title="Earnings summary"
                      rows={earningsSummaryRows}
                      columns={columns}
                    />
                    <SummaryTable
                      title="Deductions summary"
                      rows={deductionsSummaryRows.map((row) => ({
                        ...row,
                        description: getSummaryDescription(row, "deduction"),
                      }))}
                      columns={columns}
                    />
                    <SummaryTable
                      title="Information summary"
                      rows={infoSummaryRows.map((row) => ({
                        ...row,
                        description: getSummaryDescription(row, "info"),
                      }))}
                      columns={columns}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )

  return (
    <div className="max-w-full overflow-scroll">
      <div className="mt-[40px] w-full">
        <div className="text-lg text-neutral500 font-semibold">
          Payroll Journal Report
        </div>
      </div>
      <div className="w-full">
        <></>
      </div>
    </div>
  )
}

const SummaryTable = ({
  title,
  rows,
  columns,
}: {
  title: string
  rows: any[]
  columns: any[]
}) => (
  <div className="mb-6">
    <EnhancedReportTable
      title={() => <>{title}</>}
      dataSource={rows}
      columns={columns}
      rowKey={(record: any, index?: number) =>
        `${title}-${record?.description || index}`
      }
      defaultFreezeBoundaryKey="description"
      pagination={false}
      scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
      tableLayout="auto"
      bordered
    />
  </div>
)

export interface IPayrollItem {
  payroll_item_key?: null
  payroll_item_id: number
  parent_payroll_item_id?: null
  payroll_key?: null
  payroll_id: number
  payroll_type_id?: null
  payroll_type?: null
  payroll_schedule_id?: null
  payroll_schedule?: null
  payroll_item_type_id: number
  payroll_item_type?: null
  payroll_status_id?: null
  payroll_status?: null
  payroll_item_status_id?: null
  payroll_item_status?: null
  entity_type_id?: null
  entity_type?: null
  paygrade_id: number
  paygrade: string
  annual_gross?: null
  employee_request_id?: null
  payroll_change_id?: null
  payment_reversal_id?: null
  description: string
  alias?: null
  person_key?: null
  person_id: number
  first_name: string
  middle_initial: string
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  employee_title: string
  identification_number: string
  department: string
  office_address_id?: null
  office_address_line_1?: null
  office_address_line_2?: string | null
  office_address_city?: null
  office_address_state?: null
  office_address_zip_code?: null
  office_address_country?: null
  office_full_address?: null
  office_display_name?: null
  office_address_phone?: null
  tax_office_name?: null
  tax_office_code?: null
  home_address_line_1?: null
  home_address_line_2?: null
  home_address_city?: null
  home_address_state?: null
  home_address_zip_code?: null
  home_address_country?: null
  home_full_address?: null
  pfa_name?: null
  pfa_code?: null
  allowance_group_id?: string
  allowance_group?: string
  rsa_pin?: null
  hmo_name?: null
  hmo_code?: null
  hmo_plan?: null
  nhf_epn?: null
  total_days: number
  days_worked: number
  workplace: string
  actual_amount?: null
  amount: number
  is_taxable?: null
  tax_type_id?: null
  effective_tax_rate?: null
  tax_amount?: null
  after_tax_amount?: null
  deducted_amount: number
  cummulative_deducted_amount?: null
  avatar_url?: null
  pay_period: string
  start_date: string
  cut_off_date: string
  payroll_month: string
  pay_date: string
  payment_date?: null
  nextPaymentDate?: null
  nextPaymentDate_Short?: null
  isFuturePaymentDate?: null
  isTodayPaymentDate?: null
  is_deleted: boolean
  is_active: boolean
  dateRange?: null
  document_download_title?: null
  currency?: null
  currency_code?: null
  currency_symbol: string
}

export interface IEmployeePayrollItem extends IPayrollItem {
  groups: IPayrollItem[]
  deductions: string[]
  contributions: string[]
  employeeEarnings: string[]
  amortizations: string[]
}

export interface IPayrollAmortization {
  payroll_earning_key?: null
  payroll_earning_id: number
  payroll_id: number
  pay_period: string
  payroll_change_id: number
  amount: number
  tax_amount: number
  after_tax_amount: number
  currency_code?: null
  currency_symbol: string
  description: string
  person_id: number
  first_name: string
  middle_initial: string
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  identification_number: string
  employee_title_id?: null
  employee_title: string
  department_id?: null
  department: string
  office_address_id: number
  office_display_name?: null
  avatar_url?: null
  is_deleted: boolean
  is_active: boolean
}

export interface IPayrollJournalData {
  range?: null
  grouping?: null
  report_date: string
  report_download_title: string
  payrollItems?: IPayrollItem[]
  payrollEarnings?: PayrollEarningsEntity[]
  employeeBenefits?: null
  employeeRequests?: null
  employeeLeaveBalances?: null
  payrollChanges?: null
  payrollExemptions?: null
  customFields?: null
  employeeCustomFields?: null
  employeeSummary?: null
  officeAddress: OfficeAddress
  persons?: null
  homeAddresses?: null
  nextofKins?: null
  payrollReportSummaries?: null
  payrollReportSummaryValues?: null
  payrollReportSummaryAllowances?: null
  payrollReportSummaryEarnings?: null
  statutoryBenefits?: StatutoryBenefitsEntity[] | null
  reviewRound?: null
  paygradeReportSummaries?: null
  paygradeReportSummaryAllowances?: null
  paygradeReportSummaryStatutoryBenefits?: null
  paygradeReportSummaryTaxReliefs?: null
  paygradeReportSummaryValues?: null
  paybandReportSummaries?: null
  paybandReportSummaryAllowances?: null
  paybandReportSummaryStatutoryBenefits?: null
  paybandReportSummaryValues?: null
  pensionSchedules?: null
  taxSchedules?: null
  nhfSchedules?: null
  nsitfSchedules?: null
  itfSchedules?: null
  earnedWageWithdrawals?: null
  organisationAvatarFilename?: null
  organisationName?: null
  logoUploaded: boolean | null
  colspan: number
  qualifiedPayrollDate?: null
  payPeriod?: null
  payrollStatusId: number
  employeeEarnings: boolean
  employeeTaxes: boolean
  deductions: boolean
  contributions: boolean
  totals: boolean
  customInfo: boolean
  employeeTitle: boolean
  hireDate: boolean
  department: boolean
  location: boolean
  gender: boolean
  daysWorked: boolean
  pension: boolean
  nhis: boolean
  nhf: boolean
  lifeAssurance: boolean
  currentYear: number
  employeeNumber: boolean
  paygrade: boolean
  benefit?: null
  alias: boolean
  routineAnnualGross: boolean
  monthlyGross: boolean
  routineMonthlyGross: boolean
  annualNet: boolean
  routineAnnualNet: boolean
  routineMonthlyNet: boolean
  annualAllowances: boolean
  annualStatutoryBenefits: boolean
  annualTaxReliefs: boolean
  annualTax: boolean
  createdDate: boolean
  employeeRequestTypeId: number
  liability: boolean
  pensionReferenceNumber?: null
  taxIdentificationNumber?: null
  housingReferenceNumber?: null
  nsitfReferenceNumber?: null
  itfReferenceNumber?: null
  paymentScheduleItemStatusId: number
  scheduleDate?: null
}
export interface PayrollItemsEntity {
  payroll_item_key?: null
  payroll_item_id: number
  parent_payroll_item_id?: null
  payroll_key?: null
  payroll_id: number
  payroll_type_id?: null
  payroll_type?: null
  payroll_schedule_id?: null
  payroll_schedule?: null
  payroll_item_type_id: number
  payroll_item_type?: null
  payroll_status_id?: null
  payroll_status?: null
  payroll_item_status_id?: null
  payroll_item_status?: null
  entity_type_id?: null
  entity_type?: null
  paygrade_id: number
  paygrade: string
  annual_gross?: null
  employee_request_id?: null
  payroll_change_id?: null
  payment_reversal_id?: null
  description?: string | null
  alias?: string | null
  person_key?: null
  person_id: number
  first_name: string
  middle_initial?: string | null
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  employee_title?: string | null
  identification_number: string
  department: string
  office_address_id?: number | null
  office_address_line_1?: null
  office_address_line_2?: string | null
  office_address_city?: null
  office_address_state?: null
  office_address_zip_code?: null
  office_address_country?: null
  office_full_address?: null
  office_display_name?: null
  office_address_phone?: string | null
  tax_office_name?: null
  tax_office_code?: null
  home_address_line_1?: null
  home_address_line_2?: null
  home_address_city?: null
  home_address_state?: null
  home_address_zip_code?: null
  home_address_country?: null
  home_full_address?: null
  pfa_name?: null
  pfa_code?: null
  rsa_pin?: null
  hmo_name?: null
  hmo_code?: null
  hmo_plan?: null
  nhf_epn?: null
  total_days: number
  days_worked: number
  actual_amount?: null
  amount: number
  is_taxable?: null
  tax_type_id?: number | null
  effective_tax_rate?: null
  tax_amount?: number | null
  after_tax_amount?: number | null
  deducted_amount?: number | null
  cummulative_deducted_amount?: null
  avatar_url?: null
  pay_period: string
  start_date: string
  cut_off_date: string
  payroll_month: string
  pay_date: string
  payment_date?: null
  nextPaymentDate?: null
  nextPaymentDate_Short?: null
  isFuturePaymentDate?: null
  isTodayPaymentDate?: null
  is_deleted: boolean
  is_active: boolean
  dateRange?: null
  document_download_title?: null
  currency?: null
  currency_code?: null
  currency_symbol: string
}
export interface PayrollEarningsEntity {
  payroll_earning_key?: null
  payroll_earning_id: number
  payroll_id: number
  pay_period: string
  payroll_change_id: number
  amount: number
  tax_amount: number
  after_tax_amount: number
  currency_code?: null
  currency_symbol: string
  description: string
  person_id: number
  first_name: string
  middle_initial: string
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  identification_number: string
  employee_title_id?: null
  employee_title: string
  department_id?: null
  department: string
  office_address_id: number
  office_display_name?: null
  avatar_url?: null
  is_deleted: boolean
  is_active: boolean
}
export interface OfficeAddress {
  office_address_key?: null
  office_address_id: number
  address_line_1: string
  address_line_2?: string | null
  address_city: string
  address_state: string
  state_code: string
  country: string
  country_code: string
  zip_code: string
  phone: string
  display_name?: null
  full_address?: null
  tax_office_name?: null
  tax_office_code?: null
  housing_location_name?: null
  housing_location_code?: null
  tax_id?: null
  has_children?: null
  is_deleted: boolean
  is_active: boolean
}
export interface StatutoryBenefitsEntity {
  statutory_benefit_key?: null
  statutory_benefit_id: number
  statutory_benefit_type_id?: null
  description: string
  is_deleted: boolean
  is_active: boolean
}
